import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

import { AgendaBreadcrumb, type AgendaBreadcrumbItem } from './AgendaBreadcrumb';

export type AgendaWindowDensity = 'default' | 'tight';

export interface AgendaWindowProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  breadcrumb?: readonly AgendaBreadcrumbItem[];
  onClose?: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  chips?: ReactNode;
  actions?: ReactNode;
  density?: AgendaWindowDensity;
  bodyPadded?: boolean;
  bodyStyle?: CSSProperties;
  bodyClassName?: string;
  footer?: ReactNode;
  /** Apply the modal-surface treatment (page-bg body, white header bands). */
  modal?: boolean;
  children?: ReactNode;
}

const HEAD_DENSITY: Record<AgendaWindowDensity, string> = {
  default: 'min-h-[34px] px-[14px] py-phx-4',
  tight: 'min-h-[28px] px-phx-5 py-[5px]',
};

const TITLE_DENSITY: Record<AgendaWindowDensity, string> = {
  default: 'px-[18px] pt-phx-5 pb-phx-4',
  tight: 'px-[14px] pt-phx-4 pb-phx-2',
};

const TITLE_TEXT: Record<AgendaWindowDensity, string> = {
  default: 'text-[20px]',
  tight: 'text-[16px]',
};

const CHIPS_DENSITY: Record<AgendaWindowDensity, string> = {
  default: 'px-[18px] pb-phx-5',
  tight: 'px-[14px] pb-phx-3',
};

const BODY_DENSITY: Record<AgendaWindowDensity, string> = {
  default: 'p-[14px_18px]',
  tight: 'p-phx-5',
};

/**
 * Phoenix agenda-window shell.  Provides the breadcrumb header, optional
 * title row + chip strip, scrollable body and optional footer band.  When
 * `modal` is true the body/title get the page-background tint so the window
 * reads as a layered modal surface.
 */
export function AgendaWindow({
  breadcrumb = [],
  onClose,
  title,
  subtitle,
  chips,
  actions,
  density = 'default',
  bodyPadded = true,
  bodyStyle,
  bodyClassName,
  footer,
  modal,
  children,
  className,
  ...rest
}: AgendaWindowProps) {
  return (
    <section
      data-density={density}
      data-modal={modal ? '' : undefined}
      className={cn(
        'phx-aw bg-phx-bg text-phx-text flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
        className
      )}
      {...rest}
    >
      <header
        className={cn(
          'phx-aw-head gap-phx-4 flex shrink-0 items-center',
          'bg-phx-surface border-phx-border border-b',
          HEAD_DENSITY[density]
        )}
      >
        {onClose !== undefined ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(
              'phx-aw-close -ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center',
              'rounded-phx text-phx-text-2 border-0 bg-transparent transition-colors',
              'hover:bg-phx-surface-2 hover:text-phx-text'
            )}
          >
            <Icon name="x" size={14} />
          </button>
        ) : null}
        <AgendaBreadcrumb items={breadcrumb} />
      </header>

      {title || actions || chips ? (
        <div
          className={cn(
            'phx-aw-title-row gap-phx-5 flex shrink-0 items-start',
            modal ? 'bg-phx-surface' : 'bg-phx-bg',
            TITLE_DENSITY[density]
          )}
        >
          <div className="phx-aw-title-wrap min-w-0 flex-1">
            {title ? (
              <h1
                className={cn(
                  'phx-aw-title text-phx-text m-0 truncate leading-tight font-bold tracking-[-0.01em]',
                  TITLE_TEXT[density]
                )}
              >
                {title}
              </h1>
            ) : null}
            {subtitle ? (
              <div className="phx-aw-subtitle text-phx-sm text-phx-text-muted mt-[3px] tabular-nums">
                {subtitle}
              </div>
            ) : null}
          </div>
          {actions ? (
            <div className="phx-aw-actions gap-phx-3 flex shrink-0 items-center">{actions}</div>
          ) : null}
        </div>
      ) : null}

      {chips ? (
        <div
          className={cn(
            'phx-aw-chips gap-phx-3 flex shrink-0 flex-wrap',
            modal ? 'bg-phx-surface' : 'bg-phx-bg',
            CHIPS_DENSITY[density]
          )}
        >
          {chips}
        </div>
      ) : null}

      <div
        data-padded={bodyPadded ? '' : undefined}
        style={bodyStyle}
        className={cn(
          'phx-aw-body min-h-0 flex-1 overflow-auto',
          modal && 'border-phx-border border-t',
          bodyPadded && BODY_DENSITY[density],
          bodyClassName
        )}
      >
        {children}
      </div>

      {footer ? (
        <footer
          className={cn(
            'phx-aw-foot gap-phx-4 py-phx-4 flex shrink-0 items-center px-[14px]',
            'bg-phx-surface-2 border-phx-border border-t'
          )}
        >
          {footer}
        </footer>
      ) : null}
    </section>
  );
}

export interface AgendaPillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'default' | 'primary';
}

/**
 * Inline pill used in the AgendaWindow header to display compact metrics
 * such as "3 groups · 135 pax".  Renders as a rounded chip with the
 * Phoenix surface paint.
 */
export function AgendaPill({ tone = 'default', className, children, ...rest }: AgendaPillProps) {
  return (
    <span
      data-tone={tone}
      className={cn(
        'phx-aw-pill gap-phx-3 px-phx-5 inline-flex items-baseline rounded-full border py-[3px]',
        'text-phx-sm whitespace-nowrap',
        tone === 'primary'
          ? 'bg-phx-primary-tint text-phx-primary-active border-transparent'
          : 'bg-phx-surface text-phx-text border-phx-border-2',
        className
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
